import { makeAutoObservable } from 'mobx'
import ModalState from 'reusable/mobx-helpers/ModalState'
import { tiers } from '@gap/utils/tier/tier-config'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import { apiClient } from 'api/trpc/trpc-client'
import { RequestMoreSeats, RequestSent, UpgradeTierInfo } from './OrgUpgrade'
import OrgGuardStore from '../../OrgGuardStore'
import { Timeouts } from '@gap/utils/date-utils'
import { pluralise } from '@gap/utils/language-utils'
import Loader from 'reusable/mobx-helpers/Loader'
import { snackbarError } from 'reusable/mui/snackbar/snackbar'

interface OrgSubscriptionTierStoreParams {
   orgGuardStore: OrgGuardStore
}

export class OrgSubscriptionTierStore {
   sp
   // Reset the request confirmation on close so the next open starts from the request screen again.
   upgradePlanModal = new ModalState({ onClose: () => this.resetRequest() })
   tiers = tiers
   requestLoader = new Loader(false)
   requestSent = false
   userCount: number = 0
   activeUserCount: number = 0

   constructor(sp: OrgSubscriptionTierStoreParams) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get orgId() {
      return this.sp.orgGuardStore.userOrg.orgId
   }

   get userOrg() {
      return this.sp.orgGuardStore.userOrg
   }

   get orgIsOnTrial() {
      return this.currentOrgTierName === 'Trial'
   }

   get orgIsOnExpiredTrial() {
      return this.orgIsOnTrial &&
         !!this.userOrg.trialEndDate &&
         new Date(this.userOrg.trialEndDate) <= new Date()
   }

   // NO-455 interim: orgs on one of the new plans stand in for "on Chargebee" until
   // PR #471 lands and replaces this getter with its chargebee-entitlement check.
   get orgIsOnNewPlan() {
      return !this.orgIsOnTrial && !!this.tier && !this.tier.legacy
   }

   // Two outcomes only (mirrors PR #471's gate: chargebee || isOnTrial):
   // - Orgs that can self-serve a plan change see the picker (PlanSelection): orgs already
   //   on a new plan, plus orgs still on a Trial (who convert by picking a plan).
   // - Orgs on a legacy paid tier can't self-serve, so they ask our team to add seats /
   //   update their plan. Temporary until every legacy org is migrated.
   get ViewComponent() {
      if (this.requestSent) {
         return <RequestSent />
      }
      if (this.orgIsOnNewPlan || this.orgIsOnTrial) {
         return <UpgradeTierInfo />
      }
      return <RequestMoreSeats />
   }

   get currentOrgTierName() {
      return this.sp.orgGuardStore.userData.userOrg?.tierName
   }

   // Legacy seat ceiling still reads from tier-config when the org has no explicit tierMaxUsers
   // (see OrgTeamMemberListStore.orgMaxUsers). Also feeds the orgIsOnNewPlan gate above.
   get tier() {
      return this.tiers.find(({ name }) => name === this.currentOrgTierName)
   }

   get seatsInUseText() {
      const maxUsers = this.userOrg?.tierMaxUsers
      if (typeof maxUsers === 'number') {
         return `${this.userCount} of ${maxUsers} team member ${pluralise(maxUsers, 'seat is', 'seats are')} in use.`
      }
      return `${this.userCount} team member ${pluralise(this.userCount, 'seat is', 'seats are')} in use.`
   }

   // Rounded up so a fresh 14 day trial reads 14, and the final partial day reads 1 rather than 0
   daysUntilDate = (dateString: string) => {
      return Math.ceil((new Date(dateString).getTime() - Date.now()) / Timeouts.day)
   }

   fetch = async () => {
      const userCount = await apiClient.org.getUserCountInOrg.query({ orgId: this.orgId })
      this.userCount = userCount.count
      this.activeUserCount = userCount.activeCount
   }

   // Email our sales team that this org wants more seats / a plan update, then show the confirmation screen.
   // Reuses the existing notifySalesOfUpgrade procedure (sends the advisor a confirmation and notifies sales).
   requestMoreSeats = async () => {
      await this.requestLoader.executeLoading(async () => {
         try {
            await apiClient.org.notifySalesOfUpgrade.query()
            this.requestSent = true
         } catch {
            snackbarError('We had trouble sending your request. Please try again.')
         }
      })
   }

   resetRequest = () => {
      this.requestSent = false
   }

   finishRequest = () => {
      this.upgradePlanModal.close()
   }

   handleTrialIfExpired = () => {
      if (this.orgIsOnExpiredTrial) {
         // Navigate to /clients, which is the only route we will allow unhindered use of
         this.sp.orgGuardStore.sp.regionRouterStore.navigate(`/org/${this.orgId}/clients`)

         this.upgradePlanModal.open()
         return true
      }

      return false
   }
}

export const { Provider: OrgSubscriptionTierStoreProvider, useProvidedStore: useOrgSubscriptionTierStore } =
   createStoreContext(OrgSubscriptionTierStore)
